// Hydration amounts travel the wire as a plain integer count of millilitres —
// unlike sleep durations, which need TimeSpan round-tripping. So this module
// only has to handle display formatting and the daily cap.

/** A single day can hold at most 10L of hydration across all of its entries. */
export const MAX_DAILY_HYDRATION_ML = 10_000;

/**
 * A standard glass of water. There's no universal standard, but 250ml is the
 * dominant metric convention — it's what makes the familiar "8 glasses a day"
 * advice come out at exactly 2 litres. (The US anchor is 8 fl oz ≈ 237ml,
 * which metric sources round to this same 250ml.)
 */
export const GLASS_ML = 250;

/**
 * How many whole glasses an amount represents, or null when it isn't a clean
 * multiple. 750 -> 3, 500 -> 2, 300 -> null. Used to annotate the amounts the
 * user can think of in glasses rather than millilitres.
 */
export function glassMultiple(ml: number): number | null {
  if (ml <= 0 || ml % GLASS_ML !== 0) return null;
  return ml / GLASS_ML;
}

/**
 * Per-name ordinal counters — "Water 1 / Water 2" and "Coffee 1" count
 * independently. `total` is how many entries share that name, so callers can
 * hide the ordinal when a name occurs only once.
 *
 * Sleep's computePerTypeOrdinals does the same job for exactly two types; once
 * hydration types became table rows there can be any number of them, so the
 * split is by name rather than by a boolean predicate. Entries with no name
 * group together under the empty key.
 */
export function computeOrdinalsByName(names: (string | undefined)[]): { ordinal: number; total: number }[] {
  const totals = new Map<string, number>();
  for (const name of names) {
    const key = name ?? '';
    totals.set(key, (totals.get(key) ?? 0) + 1);
  }

  const seen = new Map<string, number>();
  return names.map((name) => {
    const key = name ?? '';
    const ordinal = (seen.get(key) ?? 0) + 1;
    seen.set(key, ordinal);
    return { ordinal, total: totals.get(key) ?? 1 };
  });
}

export interface HydrationTypeTotal {
  name: string;
  ml: number;
  /** The type's `icon` column, so callers can draw a glyph without a lookup. */
  icon: string | undefined;
}

/**
 * A day's millilitres grouped by type name, largest first.
 *
 * Hydration types are rows in a table rather than a fixed pair, so no surface
 * can hard-code "water vs electrolytes" any more — every breakdown is derived
 * from whatever types the day actually contains. Ties keep first-seen order so
 * the result is stable across renders. Entries with no name (or no amount) are
 * dropped rather than grouped under a blank label.
 */
export function totalsByHydrationType(entries: { name?: string; ml?: number; icon?: string }[]): HydrationTypeTotal[] {
  const totals = new Map<string, { ml: number; icon: string | undefined }>();

  for (const entry of entries) {
    const name = entry.name;
    if (!name) continue;
    const existing = totals.get(name);
    totals.set(name, {
      ml: (existing?.ml ?? 0) + (entry.ml ?? 0),
      // First non-empty icon wins: every entry of a type carries the same one,
      // but an older entry could predate the type gaining an icon.
      icon: existing?.icon ?? entry.icon,
    });
  }

  return [...totals.entries()].map(([name, { ml, icon }]) => ({ name, ml, icon })).sort((a, b) => b.ml - a.ml);
}

const AMOUNT_FORMATS = new Map<string, Intl.NumberFormat>();

// Two decimals at most, because glass-sized steps land on quarter litres — one
// decimal would render 1250 as "1.3 L". No minimum, so whole and half litres
// stay short ("2 L", not "2.00 L").
function amountFormat(locale: string | undefined): Intl.NumberFormat {
  const key = locale || 'en-US';
  let format = AMOUNT_FORMATS.get(key);
  if (!format) {
    try {
      format = new Intl.NumberFormat(key, { maximumFractionDigits: 2 });
    } catch {
      // An unknown locale tag falls back to en-US, as formatNutritionValue does.
      format = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
    }
    AMOUNT_FORMATS.set(key, format);
  }
  return format;
}

/**
 * 500 -> "500 ml", 1250 -> "1.25 L", 1500 -> "1.5 L", 2000 -> "2 L", 0 -> "0 ml".
 * Amounts below a litre stay in millilitres; at or above, they switch to litres.
 * Unit labels are passed in so callers can supply translated units ("l"/"ml", …),
 * and the locale sets the decimal separator ("1,5 l" in Swedish). Without a
 * locale the figure is formatted as en-US.
 */
export function formatAmountLabel(ml: number, litreUnit = 'L', millilitreUnit = 'ml', locale?: string): string {
  const format = amountFormat(locale);
  if (ml < 1000) return `${format.format(ml)} ${millilitreUnit}`;
  return `${format.format(ml / 1000)} ${litreUnit}`;
}
